import React from 'react';
import PropTypes from 'prop-types';
import './ProjectCard.css';
import SharedBadge from './SharedBadge';
import { lastActivityAt } from '../utils/projectRecency';

// Technology category colors
const CATEGORY_COLORS = {
  frontend: '#3B82F6',
  backend: '#10B981',
  mobile: '#A855F7',
  quantum: '#F59E0B',
  documentation: '#6B7280'
};

const ProjectCard = ({ project, onClick, onFavoriteToggle, isFavorite = false, hideFavorite = false, selectable = false, isSelected = false, onSelectToggle, showIdHint = false, footer = null }) => {
  const getInitials = (name) => {
    if (!name) return 'UN';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return null;
    
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    
    if (days === 0) return `Updated today, ${timeStr}`;
    if (days === 1) return `Updated yesterday, ${timeStr}`;
    if (days < 7) return `Updated ${days} days ago, ${timeStr}`;
    if (days < 30) return `Updated ${Math.floor(days / 7)} weeks ago, ${timeStr}`;
    return `Updated ${date.toLocaleDateString()}, ${timeStr}`;
  };

  const getLastUpdateDate = () => {
    // Try multiple possible date field names from different sources:
    // - visitedAt: from recent_projects collection
    // - updatedAt: standard field
    // - createdAt: standard field  
    // - _updatedAt: MongoDB internal field
    // - _createdAt: MongoDB internal field
    // The latest of them (utils/projectRecency.js): the one the list is sorted by.
    const dateToUse = lastActivityAt({
      visitedAt: project.visitedAt,
      updatedAt: project.updatedAt || project._updatedAt,
      createdAt: project.createdAt || project._createdAt,
    });
    
    const formattedDate = formatDate(dateToUse);
    
    // If no valid date, try creation date fields without "Updated" prefix
    if (!formattedDate) {
      const creationDate = project.createdAt || project._createdAt;
      if (creationDate) {
        const date = new Date(creationDate);
        if (!isNaN(date.getTime())) {
          const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          return `Created ${date.toLocaleDateString()}, ${timeStr}`;
        }
      }
    }
    
    return formattedDate; // Return null if no date available
  };

  const getTechnologyColor = (technology, categories) => {
    if (!categories) return CATEGORY_COLORS.backend;
    
    // Find technology in the list
    const tech = categories.technologies?.find(t => t.value === technology);
    if (!tech || !tech.category) return CATEGORY_COLORS.backend;
    
    return CATEGORY_COLORS[tech.category] || CATEGORY_COLORS.backend;
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();

    // Simply notify parent component - don't call API directly
    if (onFavoriteToggle) {
      onFavoriteToggle(project._id);
    }
  };

  const handleSelectClick = (e) => {
    e.stopPropagation();
    if (onSelectToggle) {
      onSelectToggle(project._id);
    }
  };

  // Get technologies from project info
  const technologies = project.info?.technologies || [];
  const displayTechnologies = technologies.slice(0, 3);
  const remainingCount = technologies.length - 3;

  // Get additional project information
  const description = project.description || project.info?.description;
  const teamContact = project.info?.teamContact;
  const projectOwner = project.info?.projectOwner || project.createdBy?.name || project.creatorName;
  const environments = project.environments || [];
  const visibility = project.visibility;

  // Card border color from project
  const cardStyle = project.color ? {
    border: `3px solid ${project.color}`,
    boxShadow: `0 0 0 1px ${project.color}20`
  } : {};

  return (
    <div
      className={`project-card ${isSelected ? 'project-card-selected' : ''}`}
      onClick={onClick}
      style={cardStyle}
    >
      {/* Bulk-select checkbox */}
      {selectable && (
        <label className="project-select-checkbox" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelectClick}
          />
        </label>
      )}
      {/* Favorite star button */}
      {!hideFavorite && (
        <button
          className={`favorite-button ${isFavorite ? 'active' : ''}`}
          onClick={handleFavoriteClick}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isFavorite ? '★' : '☆'}
        </button>
      )}
      {/* Background image or placeholder */}
      <div className="project-background" style={{
        backgroundImage: project.thumbnail ? `url(${project.thumbnail})` : 'none'
      }}>
        {!project.thumbnail && (
          <div className="project-placeholder">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="24" stroke="rgba(255,255,255,0.2)" strokeWidth="2" fill="rgba(255,255,255,0.05)"/>
              <path d="M40 28v24M28 40h24" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="33" cy="35" r="2" fill="rgba(255,255,255,0.3)"/>
              <circle cx="47" cy="35" r="2" fill="rgba(255,255,255,0.3)"/>
              <path d="M32 48c2-3 4-4 8-4s6 1 8 4" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        )}
        
        {/* Gradient overlay for text readability */}
        <div className="project-overlay"></div>
      </div>
      
      <div className="project-info">
        <div className="project-creator">
          <div className="creator-avatar">
            {project.createdBy?.avatar ? (
              <img src={project.createdBy.avatar} alt={project.createdBy.name} />
            ) : (
              <span>{getInitials(project.createdBy?.name || project.creatorName || project.info?.projectOwner || 'Unknown')}</span>
            )}
          </div>
          <div className="creator-details">
            <h3 className="project-name" title={project.name}>{project.name}</h3>
            {project.sharedRole && (
              <p className="project-date">
                <SharedBadge role={project.sharedRole} />
                {project.sharedBy ? ` by ${project.sharedBy}` : ''}
              </p>
            )}
            {getLastUpdateDate() && (
              <p className="project-date">{getLastUpdateDate()}</p>
            )}
            {showIdHint && project._id && (
              <p className="project-id-hint" title="Another project shares this name">{project._id}</p>
            )}
          </div>
        </div>

        {/* Description */}
        {description && (
          <div className="project-description" title={description}>
            {description}
          </div>
        )}

        {/* Technology badges */}
        {displayTechnologies.length > 0 && (
          <div className="project-technologies">
            {displayTechnologies.map((tech, index) => (
              <span
                key={index}
                className="technology-badge"
                style={{ backgroundColor: getTechnologyColor(tech) }}
              >
                {tech}
              </span>
            ))}
            {remainingCount > 0 && (
              <span 
                className="technology-badge more-badge"
                title={`Additional technologies: ${technologies.slice(3).join(', ')}`}
              >
                +{remainingCount}
              </span>
            )}
          </div>
        )}

        {/* Additional metadata */}
        <div className="project-metadata">
          {/* Environments */}
          {environments.length > 0 && (
            <div className="project-metadata-item" title={`Environments: ${environments.map(e => e.label || e.envId).join(', ')}`}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 2a6 6 0 100 12A6 6 0 008 2zM3 8a5 5 0 1110 0A5 5 0 013 8z"/>
              </svg>
              <span>{environments.length} env{environments.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Visibility */}
          {visibility && (
            <div className="project-metadata-item" title={`Visibility: ${visibility}`}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                {visibility === 'public' ? (
                  <path d="M8 2c3.5 0 6.5 2.5 7.5 6-1 3.5-4 6-7.5 6S1.5 11.5.5 8C1.5 4.5 4.5 2 8 2zm0 10a4 4 0 100-8 4 4 0 000 8zm0-2a2 2 0 110-4 2 2 0 010 4z"/>
                ) : (
                  <path d="M8 2c-3 0-5.5 1.5-7 4l1.5 1C3.5 5.5 5.5 4 8 4s4.5 1.5 5.5 3l1.5-1C13.5 3.5 11 2 8 2zM2 8l2 2 10-10-1.5-1.5L4 7l-.5-.5L2 8z"/>
                )}
              </svg>
              <span>{visibility}</span>
            </div>
          )}

          {/* Team contact with avatar */}
          {teamContact && (
            <div className="project-metadata-item contact-item" title={`Contact: ${teamContact}`}>
              <div className="contact-avatar">
                <span>{getInitials(teamContact)}</span>
              </div>
              <span className="contact-name">{teamContact.split('@')[0]}</span>
            </div>
          )}
        </div>
        {/* Extra controls at the foot of the card (the Projects list's Start / Stop and Open app); their clicks stay theirs. */}
        {footer && <div className="project-card-run">{footer}</div>}
      </div>
    </div>
  );
};

ProjectCard.propTypes = {
  project: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string.isRequired,
    thumbnail: PropTypes.string,
    color: PropTypes.string,
    description: PropTypes.string,
    visibility: PropTypes.string,
    environments: PropTypes.arrayOf(PropTypes.shape({
      envId: PropTypes.string,
      label: PropTypes.string,
    })),
    createdBy: PropTypes.shape({
      name: PropTypes.string,
      avatar: PropTypes.string,
    }),
    creatorName: PropTypes.string,
    sharedRole: PropTypes.string,
    sharedBy: PropTypes.string,
    updatedAt: PropTypes.string,
    createdAt: PropTypes.string,
    visitedAt: PropTypes.string,
    info: PropTypes.shape({
      technologies: PropTypes.arrayOf(PropTypes.string),
      projectOwner: PropTypes.string,
      teamContact: PropTypes.string,
      description: PropTypes.string,
    }),
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  onFavoriteToggle: PropTypes.func,
  isFavorite: PropTypes.bool,
  selectable: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelectToggle: PropTypes.func,
  showIdHint: PropTypes.bool,
  footer: PropTypes.node,
};

export default ProjectCard;
